import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Sparkles, Wand2, ImagePlus, Film, Download, Loader2, Coins, Upload as UploadIcon, Play } from "lucide-react";

const IMAGE_MODES = [
  { id: "text2img", label: "Text → Image", icon: Wand2, color: "#A855F7", desc: "Describe what you want — AI creates the image." },
  { id: "img2img", label: "Image → Image", icon: ImagePlus, color: "#00E5FF", desc: "Upload a photo + prompt to transform it." },
  { id: "edit", label: "Edit Image", icon: Sparkles, color: "#FFD700", desc: "Upload a photo + tell AI what to change." },
];

const VIDEO_PROVIDERS = [
  { id: "veo3", label: "VEO-3", color: "#00E5FF", durations: [8], desc: "Google VEO-3 · 8-sec cinematic clips with audio (activation pending)." },
  { id: "sora2", label: "Sora 2 ✨ LIVE", color: "#FF2A5F", durations: [4, 8, 12], desc: "OpenAI Sora 2 · 4/8/12-sec vertical clips · powered by Emergent Universal Key." },
];

export default function AIStudio() {
  const { refresh, user } = useAuth();
  const [tab, setTab] = useState("image"); // 'image' | 'video'

  // Image state
  const [mode, setMode] = useState("text2img");
  const [prompt, setPrompt] = useState("");
  const [refFile, setRefFile] = useState(null);
  const [refB64, setRefB64] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [prices, setPrices] = useState({});

  // Video state
  const [videoProvider, setVideoProvider] = useState("sora2");
  const [videoDuration, setVideoDuration] = useState(8);
  const [videoPrices, setVideoPrices] = useState({});
  const [videoJob, setVideoJob] = useState(null); // { job_id, status, video_url, error }
  const [integrations, setIntegrations] = useState({});
  const pollRef = useRef(null);

  useEffect(() => {
    api.get("/ai/prices").then(r => setPrices(r.data || {})).catch(() => {});
    api.get("/ai/video-prices").then(r => setVideoPrices(r.data || {})).catch(() => {});
    api.get("/integrations/status").then(r => setIntegrations(r.data || {})).catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const pickRef = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { setErr("Pick an image file"); return; }
    if (f.size > 8 * 1024 * 1024) { setErr("Image must be under 8MB"); return; }
    setRefFile(f); setErr("");
    const reader = new FileReader();
    reader.onload = () => setRefB64(String(reader.result).split(",")[1]);
    reader.readAsDataURL(f);
  };

  // --- Image generation ---
  const generateImage = async () => {
    setErr(""); setResult(null);
    if (!prompt.trim()) { setErr("Type a prompt first"); return; }
    if ((mode === "img2img" || mode === "edit") && !refB64) { setErr("Upload a reference image"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/generate-image", { mode, prompt, reference_image_b64: refB64 });
      setResult(data);
      refresh?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  // --- Video generation (VEO-3 / Sora 2) ---
  const generateVideo = async () => {
    setErr(""); setVideoJob(null);
    if (!prompt.trim()) { setErr("Type a prompt first"); return; }
    // Sora 2 (via Universal Key) supports TEXT-to-video only — image is optional.
    // VEO-3 requires a reference image.
    if (videoProvider === "veo3" && !refB64) {
      setErr("VEO-3 requires a reference image (the first frame). Upload one or switch to Sora 2 (prompt-only).");
      return;
    }
    // If provider keys are not configured yet, surface a friendly Goldly message instead of the technical reason
    if (!providerEnabled) {
      setErr("Upgrade your wallet with required sufficient Goldly for this video generation.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/generate-video", {
        provider: videoProvider,
        prompt,
        reference_image_b64: refB64 || null,
        duration_seconds: videoDuration,
      });
      setVideoJob(data);
      refresh?.();
      // Begin polling
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/ai/video-job/${data.job_id}`);
          setVideoJob(prev => ({ ...prev, ...r.data }));
          if (r.data.status === "completed" || r.data.status === "failed") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (r.data.status === "failed") refresh?.(); // refund credited
          }
        } catch { /* poll error, keep retrying */ }
      }, 6000);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Video generation failed");
    } finally { setBusy(false); }
  };

  const download = () => {
    if (!result?.image_b64) return;
    const a = document.createElement("a");
    a.href = `data:${result.mime_type};base64,${result.image_b64}`;
    a.download = `hifireels_ai_${Date.now()}.png`;
    a.click();
  };

  const downloadVideo = () => {
    if (!videoJob?.video_url) return;
    window.open(videoJob.video_url, "_blank");
  };

  const activeMode = IMAGE_MODES.find(m => m.id === mode);
  const activeProvider = VIDEO_PROVIDERS.find(p => p.id === videoProvider);
  const imgCost = (id) => prices[id] ?? "—";
  const videoCost = videoPrices?.[videoProvider]?.[videoDuration] ?? "—";
  const providerEnabled = integrations[videoProvider];

  return (
    <div className="px-4 py-6 pb-24" data-testid="ai-studio-page">
      <div className="flex items-center gap-2 mb-3">
        <Link to="/" data-testid="ai-back" className="p-2 rounded-full bg-white/5 active:scale-90"><ArrowLeft className="w-4 h-4"/></Link>
        <div className="flex-1">
          <h1 className="font-outfit font-extrabold text-2xl flex items-center gap-2">
            <Wand2 className="w-6 h-6 text-[#A855F7]"/>AI Studio
          </h1>
          <p className="text-xs text-white/60">Create images & videos with AI · spend Goldly</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-white/50 uppercase">Balance</div>
          <div className="font-bold text-[#FF7A00] flex items-center gap-1"><Coins className="w-3.5 h-3.5"/>{user?.wallet_goldly ?? 0}</div>
        </div>
      </div>

      {/* Top tabs: Image | Video */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <button onClick={() => { setTab("image"); setErr(""); setVideoJob(null); }} data-testid="aistudio-tab-image"
          className={`py-2.5 rounded-2xl font-bold text-sm active:scale-95 ${tab === "image" ? "bg-[#A855F7] text-white" : "bg-white/5 border border-white/10 text-white/60"}`}>
          🎨 Images
        </button>
        <button onClick={() => { setTab("video"); setErr(""); setResult(null); }} data-testid="aistudio-tab-video"
          className={`py-2.5 rounded-2xl font-bold text-sm active:scale-95 ${tab === "video" ? "bg-[#FF2A5F] text-white" : "bg-white/5 border border-white/10 text-white/60"}`}>
          🎬 Videos
        </button>
      </div>

      {tab === "image" && (
        <>
          <div className="grid grid-cols-3 gap-2 mb-4" data-testid="ai-mode-tabs">
            {IMAGE_MODES.map(m => {
              const Icon = m.icon;
              const active = mode === m.id;
              return (
                <button key={m.id} onClick={() => { setMode(m.id); setResult(null); setErr(""); }}
                  data-testid={`ai-mode-${m.id}`}
                  className={`p-3 rounded-2xl text-left border transition active:scale-95 ${active ? "border-2 bg-white/10" : "border-white/10 bg-white/5"}`}
                  style={active ? { borderColor: m.color } : {}}>
                  <Icon className="w-5 h-5 mb-1" style={{ color: m.color }}/>
                  <div className="font-bold text-xs">{m.label}</div>
                  <div className="text-[10px] text-[#FF7A00] mt-0.5 flex items-center gap-0.5"><Coins className="w-2.5 h-2.5"/>{imgCost(m.id)}</div>
                </button>
              );
            })}
          </div>

          <p className="text-[11px] text-white/55 mb-3">{activeMode?.desc}</p>

          {(mode === "img2img" || mode === "edit") && (
            <label data-testid="ai-ref-dropzone" className="block mb-3 p-4 rounded-2xl border-2 border-dashed border-white/15 bg-white/5 cursor-pointer active:scale-[0.99]">
              <div className="flex items-center gap-2">
                <UploadIcon className="w-5 h-5 text-white/60"/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold">{refFile ? refFile.name : "Upload reference image"}</div>
                  <div className="text-[10px] text-white/50">jpg / png · up to 8MB</div>
                </div>
                {refB64 && <img src={`data:image/*;base64,${refB64}`} alt="ref" className="w-12 h-12 rounded-lg object-cover"/>}
              </div>
              <input type="file" accept="image/*" onChange={pickRef} className="hidden" data-testid="ai-ref-input"/>
            </label>
          )}

          <textarea data-testid="ai-prompt" value={prompt} onChange={e => setPrompt(e.target.value)} rows={3}
            placeholder={mode === "edit" ? "What should AI change?" : "Describe the image you want…"}
            className="w-full px-3 py-2.5 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#A855F7] text-sm mb-3"/>

          {err && <div data-testid="ai-err" className="mb-3 text-sm text-[#FF2A5F] bg-[#FF2A5F]/10 p-2.5 rounded-xl">{err}</div>}

          <button onClick={generateImage} disabled={busy} data-testid="ai-generate"
            className="w-full py-3 rounded-full font-extrabold text-black disabled:opacity-50 active:scale-95 flex items-center justify-center gap-2"
            style={{ background: activeMode?.color }}>
            {busy ? <><Loader2 className="w-4 h-4 animate-spin"/>Generating…</> : <><Sparkles className="w-4 h-4"/>Generate · {imgCost(mode)} Goldly</>}
          </button>

          {result && (
            <div className="mt-5 rounded-3xl bg-white/5 border border-white/10 p-3" data-testid="ai-result">
              <img src={`data:${result.mime_type};base64,${result.image_b64}`} alt="AI generated" className="w-full rounded-2xl mb-3" data-testid="ai-result-image"/>
              <div className="flex items-center justify-between mb-2 text-[11px] text-white/60">
                <span>Cost: <b className="text-[#FF7A00]">{result.cost} Goldly</b></span>
                <span>Balance: <b className="text-[#FF7A00]">{result.new_balance_goldly}</b></span>
              </div>
              <button onClick={download} data-testid="ai-download"
                className="w-full py-2.5 rounded-full bg-[#FF7A00] text-black font-bold flex items-center justify-center gap-2 active:scale-95">
                <Download className="w-4 h-4"/>Download Image
              </button>
            </div>
          )}
        </>
      )}

      {tab === "video" && (
        <>
          {/* Provider picker */}
          <div className="grid grid-cols-2 gap-2 mb-3" data-testid="video-provider-tabs">
            {VIDEO_PROVIDERS.map(p => {
              const active = videoProvider === p.id;
              const enabled = !!integrations[p.id];
              return (
                <button key={p.id} onClick={() => { setVideoProvider(p.id); setVideoDuration(p.durations[0]); setErr(""); setVideoJob(null); }}
                  data-testid={`video-provider-${p.id}`}
                  className={`p-3 rounded-2xl text-left border transition active:scale-95 ${active ? "border-2 bg-white/10" : "border-white/10 bg-white/5"}`}
                  style={active ? { borderColor: p.color } : {}}>
                  <div className="flex items-center gap-2 mb-1">
                    <Film className="w-5 h-5" style={{ color: p.color }}/>
                    <div className="font-bold text-sm">{p.label}</div>
                  </div>
                  <div className="text-[10px] text-white/55">{p.desc}</div>
                </button>
              );
            })}
          </div>

          {!providerEnabled && false && (
            <div className="mb-3 p-3 rounded-2xl bg-[#FF7A00]/10 border border-[#FF7A00]/30 text-[11px] text-white/80">
              <b className="text-[#FF7A00]">Activation pending.</b> The admin must add the {videoProvider === "veo3" ? "fal.ai (VEO-3)" : "OpenAI (Sora 2)"} API key in production .env. Once added, this provider activates instantly.
            </div>
          )}

          {/* Duration */}
          <div className="flex items-center gap-1.5 mb-3" data-testid="video-duration-picker">
            <span className="text-xs text-white/60 mr-1">Duration:</span>
            {(activeProvider?.durations || []).map(d => (
              <button key={d} onClick={() => setVideoDuration(d)}
                data-testid={`video-dur-${d}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold active:scale-95 ${videoDuration === d ? "bg-[#FF2A5F] text-white" : "bg-white/5 border border-white/10 text-white/70"}`}>
                {d}s
              </button>
            ))}
          </div>

          {/* Reference image — required for VEO-3, optional for Sora 2 (currently text-to-video only) */}
          <label data-testid="video-ref-dropzone" className="block mb-3 p-4 rounded-2xl border-2 border-dashed border-white/15 bg-white/5 cursor-pointer active:scale-[0.99]">
            <div className="flex items-center gap-2">
              <UploadIcon className="w-5 h-5 text-white/60"/>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold">{refFile ? refFile.name : (videoProvider === "sora2" ? "Reference image (optional)" : "Upload first-frame image")}</div>
                <div className="text-[10px] text-white/50">
                  {videoProvider === "sora2"
                    ? "Sora 2 currently runs from prompt only (image-to-video coming soon)"
                    : "jpg / png · up to 8MB"}
                </div>
              </div>
              {refB64 && <img src={`data:image/*;base64,${refB64}`} alt="ref" className="w-12 h-12 rounded-lg object-cover"/>}
            </div>
            <input type="file" accept="image/*" onChange={pickRef} className="hidden" data-testid="video-ref-input"/>
          </label>

          <textarea data-testid="video-prompt" value={prompt} onChange={e => setPrompt(e.target.value)} rows={3}
            placeholder="Describe the motion — e.g. 'camera zooms in slowly as the dancer twirls'"
            className="w-full px-3 py-2.5 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#FF2A5F] text-sm mb-3"/>

          {err && <div data-testid="video-err" data-testid-alt="ai-error" id="ai-error" className="mb-3 text-sm text-[#FF2A5F] bg-[#FF2A5F]/10 p-2.5 rounded-xl">{err}</div>}

          <button onClick={generateVideo} disabled={busy} data-testid="video-generate"
            className="w-full py-3 rounded-full font-extrabold text-white disabled:opacity-40 active:scale-95 flex items-center justify-center gap-2"
            style={{ background: activeProvider?.color }}>
            {busy ? (
              <><Loader2 className="w-4 h-4 animate-spin"/>Submitting…</>
            ) : (
              <><Film className="w-4 h-4"/>Submit · {videoCost} Goldly</>
            )}
          </button>

          {videoJob && (
            <div className="mt-5 rounded-3xl bg-white/5 border border-white/10 p-3" data-testid="video-job">
              {videoJob.status !== "completed" && videoJob.status !== "failed" && (
                <div className="flex items-center gap-3 p-4">
                  <Loader2 className="w-5 h-5 animate-spin text-[#FF2A5F]"/>
                  <div>
                    <div className="font-bold text-sm capitalize">{videoJob.status}…</div>
                    <div className="text-[10px] text-white/50">Video gen can take 1–3 minutes. You can leave this page; the wallet auto-refunds on failure.</div>
                  </div>
                </div>
              )}
              {videoJob.status === "completed" && videoJob.video_url && (
                <>
                  <video src={videoJob.video_url} controls className="w-full rounded-2xl mb-3" data-testid="video-result-player"/>
                  <button onClick={downloadVideo} data-testid="video-download"
                    className="w-full py-2.5 rounded-full bg-[#FF7A00] text-black font-bold flex items-center justify-center gap-2 active:scale-95">
                    <Download className="w-4 h-4"/>Open / Download Video
                  </button>
                </>
              )}
              {videoJob.status === "failed" && (
                <div className="text-sm text-[#FF2A5F]">
                  <b>Generation failed.</b> {videoJob.error || ""} Your Goldly has been refunded.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
